import * as React from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import { cn } from "../../../lib/utils";
import { markdownToHtmlSync, htmlToMarkdown } from "../../../lib/wysiwygUtils";
import { Citation } from "./CitationExtension";

export interface WysiwygEditorProps {
  /** Initial content in markdown format */
  initialContent: string;
  /** Callback when content changes, receives markdown */
  onChange: (markdown: string) => void;
  /** Callback when user presses Cmd/Ctrl+Enter */
  onSave?: () => void;
  /** Callback when user presses Escape */
  onCancel?: () => void;
  /** Placeholder text when editor is empty */
  placeholder?: string;
  /** Auto focus the editor on mount */
  autoFocus?: boolean;
  /** Additional CSS classes for the wrapper */
  className?: string;
  /** CSS classes for the editor element (base styling like padding, colors, etc.) */
  editorClassName?: string;
}

function WysiwygEditor({
  initialContent,
  onChange,
  onSave,
  onCancel,
  placeholder = "Enter your content...",
  autoFocus = true,
  className,
  editorClassName,
}: WysiwygEditorProps) {
  // Convert initial markdown to HTML for TipTap
  const initialHtml = React.useMemo(() => {
    return markdownToHtmlSync(initialContent);
  }, [initialContent]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3, 4, 5, 6],
        },
        bulletList: {
          keepMarks: true,
          keepAttributes: false,
        },
        orderedList: {
          keepMarks: true,
          keepAttributes: false,
        },
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        linkOnPaste: true,
      }),
      Placeholder.configure({
        placeholder,
      }),
      Citation,
    ],
    content: initialHtml,
    autofocus: autoFocus ? "end" : false,
    editorProps: {
      attributes: {
        class: cn(
          // Base editor styles (can be overridden via editorClassName)
          editorClassName,
          // Prose styles for rich text rendering
          "prose prose-sm max-w-none",
          "prose-headings:text-text-primary prose-headings:font-semibold prose-headings:leading-tight",
          "prose-h1:text-2xl prose-h2:text-xl prose-h3:text-lg prose-h4:text-base",
          "prose-p:my-2 prose-p:leading-relaxed",
          "prose-ul:my-2 prose-ul:list-disc prose-ul:pl-6",
          "prose-ol:my-2 prose-ol:list-decimal prose-ol:pl-6",
          "prose-li:my-0.5",
          "prose-a:text-link-primary prose-a:underline",
          "prose-strong:text-text-primary prose-strong:font-semibold",
          "prose-em:italic",
          "prose-code:bg-surface-secondary prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-sm",
          "prose-pre:bg-surface-secondary prose-pre:p-3 prose-pre:rounded-lg",
          "prose-blockquote:border-l-4 prose-blockquote:border-stroke-secondary prose-blockquote:pl-4 prose-blockquote:italic",
          // Placeholder styling
          "[&_.is-editor-empty:first-child::before]:text-text-tertiary [&_.is-editor-empty:first-child::before]:content-[attr(data-placeholder)] [&_.is-editor-empty:first-child::before]:float-left [&_.is-editor-empty:first-child::before]:h-0 [&_.is-editor-empty:first-child::before]:pointer-events-none",
          // Citation marker styling - non-editable inline citations
          "[&_.citation-marker]:inline [&_.citation-marker]:text-link-primary [&_.citation-marker]:text-xs [&_.citation-marker]:font-medium [&_.citation-marker]:align-super [&_.citation-marker]:cursor-default [&_.citation-marker]:select-none",
        ),
      },
      handleKeyDown: (_view, event) => {
        // Cmd/Ctrl + Enter to save
        if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
          event.preventDefault();
          onSave?.();
          return true;
        }

        // Escape to cancel
        if (event.key === "Escape") {
          event.preventDefault();
          onCancel?.();
          return true;
        }

        return false;
      },
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      const markdown = htmlToMarkdown(html);
      onChange(markdown);
    },
  });

  // Sync content when initialContent changes externally
  React.useEffect(() => {
    if (editor && initialContent !== undefined) {
      const currentMarkdown = htmlToMarkdown(editor.getHTML());
      if (currentMarkdown !== initialContent) {
        const newHtml = markdownToHtmlSync(initialContent);
        editor.commands.setContent(newHtml);
      }
    }
  }, [editor, initialContent]);

  // Clean up editor on unmount
  React.useEffect(() => {
    return () => {
      editor?.destroy();
    };
  }, [editor]);

  return (
    <div className={cn("flex min-h-0 flex-1 flex-col", className)}>
      <EditorContent editor={editor} className="flex min-h-0 flex-1 flex-col" />
    </div>
  );
}

WysiwygEditor.displayName = "WysiwygEditor";

export { WysiwygEditor };
